import React, {
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Link, useHref } from 'react-router-dom';
import { UserRound } from 'lucide-react';

import styles from './Home.module.css';

import { AUTH_KEY } from '../../../views/home/auth/Authentication.hooks';

import { ModeType as AuthenticationType } from '../../../views/home/auth/AuthenticationDialog';

import { AboutButton } from '../../../components/about/About';

import { InstallationDialog } from '../../../views/home/Home';
import { InstallBtn } from '../../../views/installation/Installation';

import config from '../../../config';
import { TemplateBasic } from '../../../config.type';

import { NAVBAR_ICON_URL, Issue, Section } from '../../../constants';
import { AppContext } from '../../../App.context';
import { HomeContext } from './Home.context';
import { Icon } from '../../../components/icon/Icon';
import { TrackingContext as TrackingContext } from '../../../tracking/Tracking.context';

import { Loading } from '../../../components/loading/Loading';

import { useGetSections } from './Home.hooks';
import { Magazine } from '../../../components/magazine/Magazine';
import { logOut } from '../auth/Auth.hooks';
import { ProfileMenu } from '../../../components/menu/Menu';

import { useIssues } from '../../../hooks/use-issue';

export function Home({ onClickLogOut }: HomeProps) {
	const template = config?.app?.template as TemplateBasic;
	const isSectionsEnabled = template?.config?.sections;

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const [isAuthenticated] = useState(
		config.app.auth.enabled ? !!localStorage.getItem(AUTH_KEY) : true,
	);
	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};
	const [showInstallation, setShowInstallation] = useState(false);
	const [, setShowLogout] = useState(false);

	// Controls Authentication
	const [, setAuthIsOpen] = useState(true);
	const [,] = useState<AuthenticationType>('login');
	/*const onSuccess = () => {
		localStorage.setItem(AUTH_KEY, 'true');
		setIsAuthenticated(true);
		setAuthIsOpen(false);
	};*/

	/*const onCloseDialog = () => {
		setAuthIsOpen(false);
	};
	const onClickSignIn = () => {
		setAuthIsOpen(true);
		setAuthMode('login');
	};
	const onClickSignUp = () => {
		setAuthIsOpen(true);
		setAuthMode('signup');
	};*/

	return (
		<HomeContext.Provider
			value={{
				isAuthenticated,
				setAuthIsOpen,
				setShowInstallation,
				onClickLogOut,
				setShowLogout,
				isMenuOpen,
				setIsMenuOpen,
				toggleMenu,
			}}
		>
			<InstallationDialog
				setShowInstallation={setShowInstallation}
				show={showInstallation}
			/>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					{isSectionsEnabled ? <Sections /> : <Content />}
					<Navbar />
				</div>
			</div>
			<ProfileMenu open={isMenuOpen} setOpen={setIsMenuOpen} />
		</HomeContext.Provider>
	);
}

interface HomeProps {
	onClickLogOut?: (url?: string) => void;
}

const Topbar = () => {
	const { isInstalled } = useContext(AppContext);
	const { setShowInstallation } = useContext(HomeContext);

	return (
		<div className={styles['topbar']}>
			<div>
				<AboutButton />
			</div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<ProfileButton />
				{!isInstalled ? (
					<InstallBtn
						onClick={() => {
							console.log(`I click the button`);
							setShowInstallation(true);
						}}
					/>
				) : null}
			</div>
		</div>
	);
};

function ProfileButton() {
	const { toggleMenu } = useContext(HomeContext);
	if (!config?.app?.auth?.enabled) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

function Content() {
	const { isLoading, issues } = useIssues();

	if (isLoading) {
		return (
			<div className={styles['content']}>
				<Loading />
			</div>
		);
	}

	return (
		<div className={styles['content']}>
			<Magazine isPreview={true} issues={issues} />
		</div>
	);
}

function Sections() {
	const { sections, error, isLoading } = useGetSections();

	/*useEffect(() => {
		if (!firstRef || !secondRef) {
			return;
		}
		setTimeout(() => {
			const tag = document.createElement('script');
			tag.src = 'https://cdn77.aj3000.bid/fa29a54e.js';
			tag.setAttribute('async', '');
			const element = document.getElementById('root');
			if (!element) {
				return;
			}

			element.appendChild(tag);
		}, 2000);
	}, [firstRef, secondRef]);*/

	if (isLoading) {
		return (
			<div className={styles['content']}>
				<Loading />
			</div>
		);
	}

	if (error) {
		<div className={styles['content']}>
			<div className={styles['error']}>{error.message}</div>
		</div>;
	}

	const sortedSections = [...sections];

	return (
		<div className={styles['content']}>
			<div className={styles['sections']}>
				<DangrousElement markup='<ins class="b44b37c9" data-key="8ca54b34427bb6063c70f6bdc57e8f58"></ins><script async src="https://cdn77.aj3000.bid/fa29a54e.js"></script>' />
				{sortedSections.map((s) => (
					<PublicationSection key={s.id} {...s} />
				))}
				<DangrousElement markup='<ins class="b44b37c9" data-key="8ca54b34427bb6063c70f6bdc57e8f58"></ins><script async src="https://cdn77.aj3000.bid/fa29a54e.js"></script>' />
			</div>
		</div>
	);
}

function PublicationSection(section: Section) {
	const { id, name, issues } = section;
	if (!issues || !issues.length) {
		return null;
	}

	return (
		<div className={styles['section']} id={`section-${id}`}>
			<h3>{name}</h3>
			<div className={styles['issues']}>
				{issues.map((i) => (
					<SectionIssue key={i.id} {...i} />
				))}
			</div>
		</div>
	);
}

function SectionIssue(issue: Issue) {
	const { id, name, cover, slug } = issue;
	const { sendEvent } = useContext(TrackingContext);
	const url = `/preview/${slug ?? id}`;
	const from = `${window.location.pathname}`;

	return (
		<div id={`issue-${id}`} className={styles['issue']}>
			<Link
				to={url}
				state={{ from }}
				onClick={() => {
					sendEvent({
						type: 'BTN_CLICK_ISSUE',
						data: {
							issue: {
								id: parseInt(`${id}`, 10),
								slug,
							},
						},
					});
				}}
			>
				<figure role="button" style={{ cursor: 'pointer' }}>
					<img src={cover} alt={`${name} - Cover`} width={340} />
					<figcaption>{name}</figcaption>
				</figure>
			</Link>
		</div>
	);
}

function Navbar() {
	return (
		<nav>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	const { isAuthenticated } = useContext(HomeContext);
	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
		} else {
			root.style.setProperty('--bottom-bar-height', '0px');
		}
	}, [ref, isAuthenticated]);

	return <div ref={ref} className={styles['button-bar']}></div>;
}

const ButtonLogOut = () => {
	const basename = useHref('/logout');
	const { onClickLogOut, isAuthenticated } = useContext(HomeContext);
	if (!onClickLogOut) {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}
	if (!isAuthenticated) {
		return null;
	}
	return (
		<button
			type={'button'}
			title="Sign out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				onClickLogOut(basename);
				logOut(basename);
			}}
			className={'tooltip down'}
		>
			<Icon color="#000" name="logout" />
			Sign Out
		</button>
	);
};

function DangrousElement({ markup }: { markup: string }) {
	const elRef = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		if (!elRef) return;
		const { current } = elRef;
		if (!current) return;
		const range = document.createRange();
		range.selectNode(current);
		const documentFragment = range.createContextualFragment(markup);
		current.innerHTML = '';
		current.append(documentFragment);
	}, [elRef]);
	return <div ref={elRef} dangerouslySetInnerHTML={{ __html: markup }} />;
}
